// Firmas del trabajador (M45): asistencia a inducciones y capacitaciones (Anexo 1 TH 6 y TH 9), entrega-recepción de
// EPP (PO 22) y certificados de aptitud médica (PO 4). Tipos, buckets y etiquetas para la pantalla.
// Decisión (decidido por Claude, la revisa Kevin al validar; Ley de Comercio Electrónico, Firmas Electrónicas y
// Mensajes de Datos, Art. 14): firma electrónica simple dibujada en la pantalla de un usuario con sesión.

export const BUCKET_FIRMAS = 'worker-signatures';        // PNG y JPEG hasta 5 MB (firmas y fotos del papel firmado)
export const BUCKET_DOCUMENTOS = 'sso-documents';        // PDF (certificado del médico, escaneo en PDF)
export type BucketRegistro = typeof BUCKET_FIRMAS | typeof BUCKET_DOCUMENTOS;

// M49 (GT 37): 'socializacion' = la socialización de un procedimiento aprobado de trabajo especial; firman la lista de
// asistentes y quien socializa (rol tecnico), como la asistencia.
export type TipoRegistro = 'asistencia' | 'epp_entrega' | 'aptitud_medica' | 'socializacion';
export type EstadoRegistro = 'abierto' | 'en_firma' | 'firmado';
export type MetodoRegistro = 'pantalla' | 'papel';
export type RolFirmante = 'trabajador' | 'tecnico' | 'responsable';
export type ClaseCapacitacion = 'induccion' | 'capacitacion' | 'entrenamiento';
export type ResultadoEvaluacion = 'aprobado' | 'no_aprobado';

export interface ReaperturaRegistro {
  reabierto_en: string; reabierto_por: string | null; motivo: string; estado: EstadoRegistro; firmado_en: string | null;
  metodo: MetodoRegistro | null; hash: string | null; firmas: unknown[];
}

export interface FilaRegistro {
  id: string;
  company_id: string;
  kind: TipoRegistro;
  training_id: string | null;
  medical_exam_id: string | null;
  worker_id: string | null;
  procedure_version_id?: string | null;   // M49: socializacion
  record_date: string;
  status: EstadoRegistro;
  snapshot: Contenido | null;
  content_hash: string | null;
  sign_method: MetodoRegistro | null;
  signed_at: string | null;
  signed_by: string | null;
  scan_bucket: BucketRegistro | null;
  scan_path: string | null;
  doctor_cert_bucket: BucketRegistro | null;
  doctor_cert_path: string | null;
  reopen_history: ReaperturaRegistro[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface FilaFirma {
  id: string;
  company_id: string;
  record_id: string;
  role: RolFirmante;
  worker_id: string | null;
  signer_name: string;
  signer_cedula: string | null;
  image_path: string;
  content_hash: string;
  signed_at: string;
  created_by: string | null;
}

// ── Contenido congelado (snapshot) por tipo ──

export interface AsistenteContenido {
  worker_id: string; nombre: string; cedula: string; evaluacion: ResultadoEvaluacion | null; nota: number | null;
}
export interface ContenidoAsistencia {
  tipo: 'asistencia';
  capacitacion_id: string;
  empresa: string;
  clase: ClaseCapacitacion;
  fecha: string;
  titulo: string;
  tema: string;
  tecnico: string;
  material: string;
  evaluacion: string;
  duracion_horas: number | null;
  asistentes: AsistenteContenido[];
}

export interface ItemEppContenido {
  asignacion_id: string; epp: string; categoria: string | null; marca: string | null; modelo: string | null;
  condicion: string | null; vence: string | null;
}
export interface ContenidoEpp {
  tipo: 'epp_entrega';
  worker_id: string;
  empresa: string;
  trabajador: string;
  cedula: string;
  fecha: string;
  items: ItemEppContenido[];
}

export interface ContenidoAptitud {
  tipo: 'aptitud_medica';
  examen_id: string;
  worker_id: string;
  empresa: string;
  trabajador: string;
  cedula: string;
  fecha: string;
  tipo_examen: string;
  aptitud: string;
  medico: string;
}

/** Socialización de un procedimiento aprobado (M49, GT 37): el procedimiento, quien lo socializa y la lista. */
export interface ContenidoSocializacion {
  tipo: 'socializacion';
  version_id: string;
  empresa: string;
  fecha: string;
  titulo: string;          // procedimiento y versión
  tema: string;
  tecnico: string;         // quien socializa
  material: string;
  evaluacion: string;      // opcional
  duracion_horas: number | null;
  asistentes: AsistenteContenido[];
}

export type Contenido = ContenidoAsistencia | ContenidoEpp | ContenidoAptitud | ContenidoSocializacion;
export type ContenidoConLista = ContenidoAsistencia | ContenidoSocializacion;
/** ¿El registro tiene lista de asistentes que firman (asistencia o socialización)? */
export const conLista = (c: Contenido): c is ContenidoConLista => c.tipo === 'asistencia' || c.tipo === 'socializacion';
export const tipoConLista = (k: TipoRegistro) => k === 'asistencia' || k === 'socializacion';

// ── Etiquetas ──

export const ETIQUETA_CLASE: Record<ClaseCapacitacion, string> = {
  induccion: 'Inducción', capacitacion: 'Capacitación', entrenamiento: 'Entrenamiento',
};
export const ETIQUETA_TIPO_REGISTRO: Record<TipoRegistro, string> = {
  asistencia: 'Registro de asistencia', epp_entrega: 'Registro de entrega-recepción de EPP', aptitud_medica: 'Certificado de aptitud médica',
  socializacion: 'Registro de socialización',
};
export const ETIQUETA_ROL: Record<RolFirmante, string> = {
  trabajador: 'Trabajador', tecnico: 'Técnico o capacitador', responsable: 'Responsable de la entrega',
};
export const ETIQUETA_ESTADO_REGISTRO: Record<EstadoRegistro, string> = {
  abierto: 'Sin preparar', en_firma: 'En firma', firmado: 'Firmado',
};
export const ETIQUETA_METODO: Record<MetodoRegistro, string> = {
  pantalla: 'firma en pantalla', papel: 'formato impreso firmado y escaneado',
};
export const ETIQUETA_EVALUACION: Record<ResultadoEvaluacion, string> = { aprobado: 'Aprobado', no_aprobado: 'No aprobado' };
export const ETIQUETA_TIPO_EXAMEN: Record<string, string> = {
  pre_ocupacional: 'Ingreso (preocupacional)', periodico: 'Periódico', reintegro: 'Reintegro', retiro: 'Retiro',
};
export const ETIQUETA_APTITUD: Record<string, string> = {
  apto: 'Apto', apto_con_restricciones: 'Apto con restricciones', no_apto: 'No apto', pendiente: 'Pendiente',
};

/** Marca para un registro o una firma cuyo trabajador se borró: el nombre y la cédula salen del contenido firmado. */
export const MARCA_TRABAJADOR_ELIMINADO = '(trabajador eliminado)';

/** Texto legal que acompaña la firma (pantalla y PDF). */
export const TEXTO_FIRMA_SIMPLE =
  'Firma electrónica simple (Ley de Comercio Electrónico, Firmas Electrónicas y Mensajes de Datos, Art. 14): '
  + 'trazo, nombre y cédula del firmante, fecha y hora del servidor y huella SHA-256 del contenido firmado.';
